import { useSelector } from 'react-redux';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import type {
  ExistingRequest,
  ExistingRequestBody,
  GmrDataItem,
  StatusItem,
  VehicleInfo,
  ProviderInfo,
} from '../../types';

interface TransformedStatus {
  status: {
    display: string;
    statusTimeUTC: string;
  };
  eta?: string[];
  vehicle?: VehicleInfo;
  provider?: ProviderInfo;
  dispatchCenter?: Record<string, unknown>;
  updatedBy?: Record<string, unknown>;
  timeNegotiation?: Record<string, unknown>;
}

interface FormattedLatestStatus {
  status: {
    display: string;
    statusTimeUTC: string;
  };
  eta: string[];
  vehicle: VehicleInfo | Record<string, never>;
  provider: ProviderInfo | Record<string, never>;
  dispatchCenter: Record<string, unknown>;
  updatedBy: Record<string, unknown>;
  timeNegotiation: Record<string, unknown>;
}

interface LatestDataResult {
  latestVehicle: VehicleInfo | null;
  latestProvider: ProviderInfo | null;
  latestAirDetails: Record<string, unknown> | null;
}

interface UseExistingRequestReturn {
  existingRequest: ExistingRequest | null;
  existingRequestBody: ExistingRequestBody | undefined;
  gmrData: GmrDataItem[] | undefined;
  latestStatus: FormattedLatestStatus | null;
  remainingStatuses: TransformedStatus[];
  transformedStatuses: TransformedStatus[];
  latestEta: string | undefined;
  latestVehicle: VehicleInfo | null;
  latestProvider: ProviderInfo | null;
  latestAirDetails: Record<string, unknown> | null;
}

export const transformStatuses = (
  gmrData: GmrDataItem[] | undefined | null,
  firstStatus: StatusItem | undefined,
): TransformedStatus[] => {
  const transformedFirstStatus: TransformedStatus[] = firstStatus
    ? [
        {
          status: {
            display: firstStatus.update || '',
            statusTimeUTC: firstStatus.timestamp || '',
          },
        },
      ]
    : [];

  const allStatuses: TransformedStatus[] = [
    ...transformedFirstStatus,
    ...((gmrData || []) as TransformedStatus[]),
  ];

  // Filter out consecutive duplicates
  const result = allStatuses.filter(
    (status: TransformedStatus, index: number, array: TransformedStatus[]) => {
      if (index === 0) return true;
      const currentDisplay = status.status?.display;
      const prevDisplay = array[index - 1].status?.display;
      return currentDisplay !== prevDisplay;
    },
  );

  return result;
};

export const getLatestData = (
  gmrData: GmrDataItem[] | undefined | null,
): LatestDataResult => {
  if (!gmrData || !Array.isArray(gmrData) || gmrData.length === 0) {
    return {
      latestVehicle: null,
      latestProvider: null,
      latestAirDetails: null,
    };
  }

  let latestVehicle: VehicleInfo | null = null;
  let latestProvider: ProviderInfo | null = null;
  let latestAirDetails: Record<string, unknown> | null = null;

  // Since the array is in chronological order, we can iterate from the end
  for (let i = gmrData.length - 1; i >= 0; i--) {
    const currentItem = gmrData[i];

    if (!currentItem || typeof currentItem !== 'object') {
      continue;
    }

    if (
      !latestVehicle &&
      currentItem.vehicle &&
      typeof currentItem.vehicle === 'object' &&
      currentItem.vehicle.id
    ) {
      latestVehicle = currentItem.vehicle;
    }

    if (
      !latestProvider &&
      currentItem.provider &&
      typeof currentItem.provider === 'object' &&
      currentItem.provider.name &&
      currentItem.provider.name.trim() !== ''
    ) {
      latestProvider = currentItem.provider;
    }

    if (
      !latestAirDetails &&
      currentItem.airDetails &&
      typeof currentItem.airDetails === 'object'
    ) {
      latestAirDetails = currentItem.airDetails;
    }

    // If we found all three, we can break early
    if (latestVehicle && latestProvider && latestAirDetails) {
      break;
    }
  }

  return {
    latestVehicle,
    latestProvider,
    latestAirDetails,
  };
};

export const useExistingRequest = (): UseExistingRequestReturn => {
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;
  const existingRequestBody = existingRequest?.body;
  const gmrData = existingRequestBody?.gmr_data;
  const firstStatus = existingRequestBody?.status?.[0];

  const transformedStatuses = transformStatuses(gmrData, firstStatus);
  const latestEta = existingRequestBody?.eta;
  const { latestVehicle, latestProvider, latestAirDetails } =
    getLatestData(gmrData);

  let latestStatus: TransformedStatus | null = null;
  let remainingStatuses: TransformedStatus[] = [];

  if (transformedStatuses?.length) {
    const sortedStatuses = [...transformedStatuses].sort(
      (a, b) =>
        new Date(b.status?.statusTimeUTC || 0).getTime() -
        new Date(a.status?.statusTimeUTC || 0).getTime(),
    );

    latestStatus = sortedStatuses[0];
    remainingStatuses = sortedStatuses.slice(1);
  }

  // Ensure we're returning the status object in the expected format
  const formattedLatestStatus: FormattedLatestStatus | null = latestStatus
    ? {
        status: {
          display: latestStatus.status?.display || '',
          statusTimeUTC: latestStatus.status?.statusTimeUTC || '',
        },
        eta: latestStatus.eta || [],
        vehicle: latestStatus.vehicle || {},
        provider: latestStatus.provider || {},
        dispatchCenter: latestStatus.dispatchCenter || {},
        updatedBy: latestStatus.updatedBy || {},
        timeNegotiation: latestStatus.timeNegotiation || {},
      }
    : null;

  /**
   * @returns {Object} Hook return object containing existing request data and processed status information
   * @returns {Object|null} returns.existingRequest - The complete existing request object from Redux store
   * @returns {Object|null} returns.existingRequestBody - The body portion of the existing request containing main data
   * @returns {Array|null} returns.gmrData - Array of GMR (General Medical Record) data entries in chronological order
   * @returns {Object|null} returns.latestStatus - The most recent status formatted with display, statusTimeUTC, eta, vehicle, provider, dispatchCenter, updatedBy, and timeNegotiation properties
   * @returns {Array} returns.remainingStatuses - Array of all statuses except the latest one, sorted by timestamp (newest first)
   * @returns {Array} returns.transformedStatuses - All statuses (including first status) with consecutive duplicates removed
   * @returns {*} returns.latestEta - The latest ETA information from the existing request body
   * @returns {Object|null} returns.latestVehicle - The most recent vehicle data object with id property, or null if not found
   * @returns {Object|null} returns.latestProvider - The most recent provider data object, or null if not found
   * @returns {Object|null} returns.latestAirDetails - The most recent air details data object, or null if not found
   */
  return {
    existingRequest,
    existingRequestBody,
    gmrData,
    latestStatus: formattedLatestStatus,
    remainingStatuses,
    transformedStatuses,
    latestEta,
    latestVehicle,
    latestProvider,
    latestAirDetails,
  };
};
